import React, { useState, useEffect, Suspense, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { updateSingleCart, updateMultiCart } from "../../store/reducers/subscripion";
import BillingInformationForm from "./checkoutForm/billingInformationForm";
import OrderSummaryForm from "./checkoutForm/orderSummaryForm";
import PaymentForm from "./checkoutForm/paymentForm";
import UpdateBillingAddressDialog from "./checkoutForm/updateBillingAdress";
import AccountCreationForm from "./checkoutForm/accountCreationForm";
import useAuth from "../../hooks/useAuth";

import { apiService } from "../../api/apiAdapter";

const NavBar = React.lazy(() => import("../../components/navBar"));
const TopicBanner = React.lazy(() => import("../../components/SubscribeTopicBanner"));
const Footer = React.lazy(() => import("../../components/footer"));

const formatToMidnightUTC = (date) => {
  const value = new Date(date);
  value.setUTCHours(0, 0, 0, 0);
  return value.toISOString().replace(/\.000Z$/, "Z");
};

const getNewSubscriptionDates = (item) => {
  if (item.startDate && item.endDate) {
    return { startDate: item.startDate, endDate: item.endDate };
  }

  const startDate = new Date();
  const endDate = new Date(startDate);
  endDate.setUTCFullYear(endDate.getUTCFullYear() + 1);

  return {
    startDate: formatToMidnightUTC(startDate),
    endDate: formatToMidnightUTC(endDate),
  };
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const { email } = useAuth();

  useEffect(() => {
    const initCart = async () => {
      if (location.state?.cartItems?.length) {
        setCartItems(
          Array.isArray(location.state.cartItems)
            ? location.state.cartItems
            : [location.state.cartItems],
        );
        return;
      }

      try {
        const items = await apiService.getCart();
        setCartItems(items || []);
      } catch (e) {
        console.error("Failed to fetch cart in checkout", e);
        setCartItems([]);
      }
    };

    initCart();
  }, [location.state]);

  const [checkoutProcess, setCheckoutProcess] = useState("billingInformation");
  const [paymentType, setPaymentType] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [contactDetails, setContactDetails] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [billingAddress, setBillingAddress] = useState({});
  const [isBillingAddressDialogOpen, setBillingAddressDialogOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (email) {
      setIsExistingUser(true);
    }
  }, [email]);

  const openBillingAddressDialog = () => setBillingAddressDialogOpen(true);
  const closeBillingAddressDialog = () => setBillingAddressDialogOpen(false);

  const handlePriceUpdate = useCallback((price) => {
    if (typeof price === "number") setTotalAmount(price);
  }, []);

  const handleQuantityUpdate = (newQuantity, cartsid) => {
    setCartItems((prev) =>
      prev.map((item) => (item.cartsid === cartsid ? { ...item, quantity: newQuantity } : item)),
    );
  };

  const getTitleFromGenderCode = (gendercode) => {
    switch (gendercode) {
      case 1:
        return "Mr";
      case 2:
        return "Mrs";
      case 3:
        return "Dr";
      case 4:
        return "Prof";
      case 5:
        return "AProf";
      case 6:
        return "Ms";
      default:
        return "";
    }
  };

  const getXeroDescription = (item) => {
    switch (item.ordertype) {
      case "New":
        if (item.subscriptionid === "151")
          return "Therapeutic Guidelines - single user license (+Mobile App)";
        if (item.subscriptionid === "168")
          return "Therapeutic Guidelines - multi user license (+Mobile App)";
        if (item.subscriptionid === "2151") return "ObesiD – Single User Licence";
        if (item.subscriptionid === "3151") return "Don't Rush To Crush – Single User Licence";
        break;
      case "Renew":
        if (item.subscriptionid === "151")
          return "Therapeutic Guidelines renewal - single user license (+Mobile App)";
        if (item.subscriptionid === "168")
          return "Therapeutic Guidelines renewal - multi user license (+Mobile App)";
        if (item.subscriptionid === "2151") return "ObesiD Renewal – Single User Licence";
        if (item.subscriptionid === "3151")
          return "Don't Rush To Crush Renewal – Single User Licence";
        break;
      case "Addon":
        if (item.subscriptionid === "168")
          return "Therapeutic Guidelines extra licence (+Mobile App)";
        break;
      default:
        break;
    }
    return "Therapeutic Guidelines Subscription (+Mobile App)";
  };

  // The main "checkout" flow
  const handleCheckout = async ({ isFree = false, paymentInfo = null }) => {
    if (isMountedRef.current) {
      setIsProcessingPayment(true);
    }

    let updatedContactId = contactDetails?.contactid;
    const contactEmail = isExistingUser ? email : newUserEmail;

    try {
      if (!updatedContactId) {
        const response = await apiService.getContact(contactEmail);
        if (Array.isArray(response) && response.length > 0) {
          updatedContactId = response[0].contactid;
        } else {
          throw new Error("Unable to retrieve contact ID");
        }
      }

      // 1. Prepare multiple subscription data entries
      const subscriptionData = cartItems
        .filter((item) => item.ordertype === "New")
        .map((item) => {
          const { startDate, endDate } = getNewSubscriptionDates(item);

          return {
            startdate: startDate,
            tg_expirydate: endDate,
            enddate: "2900-01-01T00:00:00Z",
            statecode: 0,
            statuscode: 0,
            emailaddress: contactEmail,
            tg_numberoflicences: item.quantity,
            isdefault: false,
            _customerid_value: updatedContactId,
            tg_subscriptionid: item.subscriptionid,
            tg_couponreferrercode: item?.tg_couponreferrercode,
          };
        });

      //Prepare transaction data (represents the whole payment)
      const transactionData = {
        tg_actualamount: totalAmount, // always updated totalAmount
        tg_orderdate: new Date().toISOString(),
        tg_ordertype: cartItems.map((item) => item.ordertype).join(","),
        tg_subscriptionid: cartItems.map((item) => item.subscriptionid).join(","),
        tg_transactionamount: parseFloat(totalAmount),
        contactId: updatedContactId,
        tg_transactionid: paymentInfo ? String(paymentInfo.transactionId) : null,
      };

      if (!isFree && paymentInfo) {
        transactionData.tg_paymentgatewayresponse = 2; // e.g., "completed"
        const types = { AMEX: 1, MASTERCARD: 2, VISA: 3, PAYPAL: 4 };
        transactionData.tg_transactiontype = types[paymentInfo.cardType] || 4;
      }

      // Prepare Xero data with multiple LineItems
      const xeroData = {
        paymentDetails: {
          type: paymentInfo?.paymentType || (isFree ? "COMPLIMENTARY" : ""),
          transactionId: paymentInfo?.transactionId || "",
          paymentDate: new Date().toISOString().split("T")[0],
        },
        contact: {
          Name: contactEmail,
          ContactNumber: contactDetails.nickname,
          FirstName: contactDetails.firstname,
          LastName: contactDetails.lastname,
          EmailAddress: contactEmail,
          PhoneNumber: contactDetails.telephone1,
          Addresses: [
            {
              addressType: "POBOX",
              addressLine1: billingAddress.address1_line1,
              city: billingAddress.address1_city,
              region: billingAddress.address1_stateorprovince,
              postalCode: billingAddress.address1_postalcode,
              country: billingAddress.address1_country,
            },
            {
              addressType: "STREET",
              addressLine1: billingAddress.address1_line1,
              city: billingAddress.address1_city,
              region: billingAddress.address1_stateorprovince,
              postalCode: billingAddress.address1_postalcode,
              country: billingAddress.address1_country,
            },
          ],
        },
        invoice: {
          LineItems: cartItems.map((item) => {
            const baseUnitAmount =
              item.originalPrice ||
              (["151", "2151", "3151"].includes(item.subscriptionid)
                ? item.individualprice
                : item.multipleprice);

            return {
              Description: getXeroDescription(item),
              Quantity: item.quantity,
              UnitAmount: baseUnitAmount,
              DiscountRate: item?.appliedDiscount || 0,
              discountMode: item?.discountMode || "percentage",
              subscriptionId: item.subscriptionid,
              orderType: item.ordertype,
            };
          }),
        },
      };

      // Handle Addons/Renewals
      const updateSubscriptionData = cartItems
        .filter((item) => ["Addon", "Renew"].includes(item.ordertype))
        .map((item) => ({
          subscriptiionType: item.ordertype,
          parentEntitlementId: item.currentsubscriptionid,
          quantity:
            item.ordertype === "Addon"
              ? item.quantity + (item.currentmultilicensenumber || 0)
              : item.quantity,
          tg_expirydate: item.endDate,
        }));

      const checkoutResponse = await apiService.createCheckout({
        subscriptionData,
        transactionData,
        xeroData,
        paymentDetails: xeroData.paymentDetails,
        updateSubscriptionData: updateSubscriptionData.length > 0 ? updateSubscriptionData : null,
        isComplimentary: isFree,
        cartId: cartItems.map((i) => i.cartsid).join(","),
      });

      if (!checkoutResponse.success) throw new Error("Backend checkout failed");
      const orderDetails = {
        status: "COMPLETED",
        create_time: new Date().toISOString(),
        contactInfo: {
          title: getTitleFromGenderCode(contactDetails.gendercode),
          firstName: contactDetails.firstname,
          lastName: contactDetails.lastname,
          company: contactDetails.companyname || null,
          address: {
            street: billingAddress.address1_line1,
            city: billingAddress.address1_city,
            state: billingAddress.address1_stateorprovince,
            postalCode: billingAddress.address1_postalcode,
            country: billingAddress.address1_country,
          },
        },
        items: cartItems.map((item) => ({
          description: getXeroDescription(item),
          validTo: item.endDate,
          price: item?.hasappliedcoupon ? item?.updatedPrice : item?.individualprice,
          quantity: item.quantity,
        })),
        receiptDetails: {
          receiptNumber: checkoutResponse.data.receiptNumber,
          paymentDate: new Date().toISOString(),
          purchaseOrderNumber: checkoutResponse.data.purchaseOrderNumber,
        },
        payment: { total: totalAmount },
      };
      navigate("/checkout/orderConfirmation", {
        state: { orderDetails },
        replace: true,
      });
    } catch (error) {
      console.error("Checkout Error:", error);
      navigate("/checkout/orderConfirmation", {
        state: {
          orderDetails: {
            success: false,
            backendstatus: false,
            paymentTransactionID: paymentInfo?.transactionId,
          },
        },
      });
    } finally {
      // Clear all possible cart types from Redux
      dispatch(updateSingleCart({ cartsId: null, quantity: 0 }));
      dispatch(updateMultiCart({ cartsId: null, quantity: 0 }));
      if (isMountedRef.current) setIsProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = (pInfo) => handleCheckout({ isFree: false, paymentInfo: pInfo });
  const handleFreeCheckout = () => handleCheckout({ isFree: true });

  const handlePaymentError = async (error) => {
    if (isMountedRef.current) {
      setIsProcessingPayment(false);
    }
    try {
      const failRes = await apiService.createFailureTransaction({
        contactId: contactDetails.contactid,
        tg_transactionresult: false,
        tg_failureresponsemessage: error.message,
        tg_actualamount: parseFloat(totalAmount),
        tg_orderdate: new Date().toISOString(),
      });
      navigate("/checkout/orderConfirmation", {
        state: {
          orderDetails: {
            success: false,
            backendstatus: true,
            status: "FAILED",
            orderNumber: failRes.tg_ordernumber,
          },
        },
      });
    } catch (e) {
      console.error("Failure recording error:", e);
    }
  };

  const handleSaveBillingAddress = (newAddress) => {
    setBillingAddress({
      ...newAddress,
      firstname: billingAddress.firstname,
      lastname: billingAddress.lastname,
    });
  };

  useEffect(() => {
    if (checkoutProcess === "orderSummary" && contactDetails) {
      setBillingAddress({
        firstname: contactDetails.firstname,
        lastname: contactDetails.lastname,
        address1_line1: contactDetails.address1_line1,
        address1_city: contactDetails.address1_city,
        address1_stateorprovince: contactDetails.address1_stateorprovince,
        address1_postalcode: contactDetails.address1_postalcode,
        address1_country: contactDetails.address1_country,
        contactid: contactDetails.contactid,
      });
    }
  }, [checkoutProcess, contactDetails]);

  const nextSteps = () => {
    if (checkoutProcess === "billingInformation") {
      isExistingUser ? setCheckoutProcess("orderSummary") : setCheckoutProcess("accountCreation");
    } else if (checkoutProcess === "accountCreation") {
      setCheckoutProcess("orderSummary");
    } else if (checkoutProcess === "orderSummary") {
      setCheckoutProcess("payment");
    }
  };

  return (
    <Suspense fallback={<div className="loader"></div>}>
      <NavBar push={navigate} />
      <TopicBanner />

      {cartItems && (
        <div className="container checkout" id="wrap">
          <div className="col-lg-12 margintp-30">
            <div className="col-lg-7 col-sm-7 col-md-7 col-xs-12 no_left_padding leftBox-xs rightalign">
              <div className="panel-group secureCheckOut" id="accordion">
                <div
                  className="panel-heading checkout-panel-heading"
                  role="tab"
                  id="headingOne"
                  style={{
                    borderBottom:
                      checkoutProcess === "billingInformation"
                        ? "4px solid #1aa2a0"
                        : "1px solid #1aa2a0",
                  }}
                >
                  <h4 className="panel-title">
                    <a
                      role="button"
                      data-toggle="collapse"
                      data-parent="#accordion"
                      href="#collapseOne"
                      aria-expanded="true"
                      aria-controls="collapseOne"
                    >
                      1. Billing Information{" "}
                    </a>
                  </h4>
                </div>

                {checkoutProcess === "billingInformation" && (
                  <BillingInformationForm
                    nextSteps={nextSteps}
                    contactDetails={contactDetails}
                    setContactDetails={setContactDetails}
                  />
                )}

                {!isExistingUser && (
                  <div
                    className="panel-heading checkout-panel-heading"
                    role="tab"
                    id="headingOne"
                    style={{
                      borderBottom:
                        checkoutProcess === "accountCreation"
                          ? "4px solid #1aa2a0"
                          : "1px solid #1aa2a0",
                    }}
                  >
                    <h4 className="panel-title">
                      <a
                        role="button"
                        data-toggle="collapse"
                        data-parent="#accordion"
                        href="#collapseOne"
                        aria-expanded="true"
                        aria-controls="collapseOne"
                      >
                        2. Account Creation
                      </a>
                    </h4>
                  </div>
                )}
                {checkoutProcess === "accountCreation" && !isExistingUser && (
                  <AccountCreationForm
                    nextSteps={nextSteps}
                    contactDetails={contactDetails}
                    setNewUserEmail={setNewUserEmail}
                    setContactDetails={setContactDetails}
                  />
                )}

                <div
                  className="panel-heading checkout-panel-heading"
                  role="tab"
                  id="headingOne"
                  style={{
                    borderBottom:
                      checkoutProcess === "orderSummary"
                        ? "4px solid #1aa2a0"
                        : "1px solid #1aa2a0",
                  }}
                >
                  <h4 className="panel-title">
                    <a
                      role="button"
                      data-toggle="collapse"
                      data-parent="#accordion"
                      href="#collapseOne"
                      aria-expanded="true"
                      aria-controls="collapseOne"
                    >
                      {isExistingUser ? 2 : 3}. Order summary{" "}
                    </a>
                  </h4>
                </div>

                {(checkoutProcess === "orderSummary" || checkoutProcess === "payment") && (
                  <OrderSummaryForm
                    cartItems={cartItems}
                    onCartItemsUpdate={(updatedItems) => setCartItems(updatedItems)}
                    updatePaymentType={setPaymentType}
                    onPriceUpdate={handlePriceUpdate}
                    onQuantityUpdate={handleQuantityUpdate}
                    onProcessFreeCheckout={handleFreeCheckout}
                    nextSteps={nextSteps}
                  />
                )}

                <div
                  className="panel-heading checkout-panel-heading"
                  role="tab"
                  id="headingOne"
                  style={{
                    borderBottom:
                      checkoutProcess === "payment" ? "4px solid #1aa2a0" : "1px solid #1aa2a0",
                  }}
                >
                  <h4 className="panel-title">
                    <a
                      role="button"
                      data-toggle="collapse"
                      data-parent="#accordion"
                      href="#collapseOne"
                      aria-expanded="true"
                      aria-controls="collapseOne"
                    >
                      {" "}
                      {isExistingUser ? 3 : 4}. Summary{" "}
                    </a>
                  </h4>
                </div>

                {paymentType !== null && (
                  <PaymentForm
                    paymentType={paymentType}
                    totalAmount={Number(totalAmount)}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    isProcessing={isProcessingPayment}
                  />
                )}
              </div>
            </div>

            <div className="col-lg-5 col-sm-5 col-md-5 col-xs-12 margintop10px rightBox-xs leftBox-xs leftAlign">
              <h4 className="checkout-procress section-heading hidden-xs">
                Your checkout progress
              </h4>
              <ul className="step_listing">
                <li>
                  <div
                    href="#"
                    className={checkoutProcess === "billingInformation" ? "active" : ""}
                    id="billingInformationHeading"
                  >
                    <span className={checkoutProcess === "billingInformation" ? "active" : ""}>
                      1
                    </span>
                    Billing information
                  </div>
                </li>
                {!isExistingUser && (
                  <li>
                    <div
                      href="#"
                      className={checkoutProcess === "accountCreation" ? "active" : ""}
                      id="accountCreationHeading"
                    >
                      <span className={checkoutProcess === "accountCreation" ? "active" : ""}>
                        2
                      </span>
                      Account creation
                    </div>
                  </li>
                )}
                <li>
                  <div
                    href="#"
                    className={checkoutProcess === "orderSummary" ? "active" : ""}
                    id="orderSummaryHeading"
                  >
                    <span className={checkoutProcess === "orderSummary" ? "active" : ""}>
                      {isExistingUser ? 2 : 3}
                    </span>
                    Order summary
                  </div>
                </li>
                <li>
                  <div
                    href="#"
                    className={checkoutProcess === "payment" ? "active" : ""}
                    id="paymentSummaryHeading"
                  >
                    <span className={checkoutProcess === "payment" ? "active" : ""}>
                      {isExistingUser ? 3 : 4}
                    </span>
                    Payment
                  </div>
                </li>
              </ul>

              {checkoutProcess === "orderSummary" && (
                <div className="row">
                  <div className="col-lg-6 col-xs-12 addBoxHeight" id="newUserAddressLocation">
                    Billing adress
                    <div className="panel adrs-info margin_btm30px">
                      <div className="adress_box">
                        <div className="pull-right" onClick={openBillingAddressDialog}>
                          <i className="fa fa-edit font20p edit_icon"></i>
                        </div>
                        <UpdateBillingAddressDialog
                          isOpen={isBillingAddressDialogOpen}
                          onClose={closeBillingAddressDialog}
                          billingAddress={billingAddress}
                          setContactDetails={setContactDetails}
                          onSave={handleSaveBillingAddress}
                          newUserEmail={newUserEmail}
                        />
                        <div className="panel-body">
                          <address>
                            <strong>
                              <span id="givenName_new">{billingAddress.firstname}</span>
                              &nbsp;
                              <span id="surName_new">{billingAddress.lastname}</span>
                            </strong>
                            <br />
                            <span id="streetAddress_new">{billingAddress.address1_line1}</span>
                            <br />
                            <span id="suburb_new">{billingAddress.address1_city}</span>
                            <br />
                            <span id="state_new">{billingAddress.address1_stateorprovince}</span>
                            &nbsp;
                            <span id="postalCode_new">{billingAddress.address1_postalcode}</span>
                            &nbsp;
                            <span id="country_new">{billingAddress.address1_country}</span>
                          </address>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!cartItems && (
        <div className="container">
          <p>
            There are no items to check out. Please{" "}
            <button
              className="btn btn-info"
              style={{
                backgroundColor: "#1aa2a0",
                borderColor: "#188f8e",
                color: "#ffffff",
                fontWeight: "600",
                borderRadius: "2px",
              }}
              onClick={() => navigate("/myCart")}
            >
              Go to your Cart
            </button>{" "}
            before proceeding.
          </p>
        </div>
      )}

      <Footer />
    </Suspense>
  );
};

export default Checkout;
